/*
 * -----------------------------------------------------------------------
 *  <copyright file="risks-reordering-grid/index.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import { useMutation } from '@apollo/client/react';
import WarningLostDataDialog from '@components-clean/legacy/unsaved-changes-dialog/warning-lost-data-dialog';
import NavigationService from '@components-clean/page-nav/navigation.service';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { LibraryRiskFieldsExtended } from '@interfaces/library-risk.interface';
import { orderBy } from '@progress/kendo-data-query';
import { DragAndDrop } from '@progress/kendo-react-common';
import { Grid, GridColumn, GridCustomCellProps } from '@progress/kendo-react-grid';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useState, useRef } from 'react';
import NotificationService from '../../../../utils/notification';
import DragHandleCell from '../../core/drag-and-drop/drag-handle-cell';
import DraggableRow from '../../core/drag-and-drop/draggable-row';
import { IExpandableGridColumnV2 } from '../../core/expandable/grid-v2/interfaces-v2/grid-props-v2.interface';
import { LibraryRiskDisplayOrder, RisksReorderingGridProps } from './risks-reordering-grid.interface';
import { useBoolean, useTranslation } from '@hooks/index';
import { UPDATE_LIBRARY_RISKS_DISPLAY_ORDER } from '@queries/legacy-risk-assessment';
import './styles.scss';
import { restructuredUpdatedCreatedBy } from '@utils-clean/update-users-array';
import { ReorderContext } from '@components/core/grid-helper';

const CUSTOM_COLUMN_WIDTH = 83;
const RisksReorderingGrid = ({
    items,
    columns,
    refetchData,
    onSaveReorder,
    closeReorder,
}: RisksReorderingGridProps) => {
    const { t } = useTranslation();

    const minGridWidth = React.useRef<number>(0);
    const grid = React.useRef<HTMLDivElement | null>(null);
    const initData = restructuredUpdatedCreatedBy(items);
    const [gridData, setGridData] = useState<LibraryRiskFieldsExtended[]>(initData);
    const activeItem = useRef<LibraryRiskFieldsExtended | null>(null);
    const [applyMinWidth, setApplyMinWidth] = React.useState(false);
    const [gridCurrent, setGridCurrent] = React.useState(0);

    const [updateLibraryRisksOrder] = useMutation(UPDATE_LIBRARY_RISKS_DISPLAY_ORDER);

    const showWarningModal = useBoolean(false);
    const orderChanged = useBoolean(false);

    useEffect(() => {
        if (items) {
            const newData = restructuredUpdatedCreatedBy(items);
            setGridData(
                orderBy(newData, [
                    { field: 'displayOrder', dir: 'asc' },
                    { field: 'updated', dir: 'desc' },
                ]),
            );
        }
    }, [items]);

    const handleResize = useCallback(
        debounce(() => {
            if (grid.current.offsetWidth < minGridWidth.current) {
                setApplyMinWidth(true);
            } else if (grid.current.offsetWidth > minGridWidth.current) {
                setGridCurrent(grid.current.offsetWidth);
                setApplyMinWidth(false);
            }
        }, 200),
        [],
    );

    React.useEffect(() => {
        grid.current = document.querySelector('.reorder-grid');
        window.addEventListener('resize', handleResize);
        columns.forEach((item: IExpandableGridColumnV2) => {
            if (item.minWidth) {
                minGridWidth.current += item.minWidth;
            }
        });
        minGridWidth.current += CUSTOM_COLUMN_WIDTH;
        setGridCurrent(grid.current.offsetWidth);
        setApplyMinWidth(grid.current.offsetWidth < minGridWidth.current);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const setWidth = (minWidth = 0) =>
        applyMinWidth ? minWidth : minWidth + (gridCurrent - minGridWidth.current) / columns.length;

    const reorder = (dataItem: LibraryRiskFieldsExtended, direction: 'before' | 'after' | null) => {
        if (activeItem.current === dataItem) {
            return;
        }
        const reorderedData = gridData.slice();
        const prevIndex = reorderedData.findIndex((p) => p === activeItem.current);
        const nextIndex = reorderedData.findIndex((p) => p === dataItem);
        const adjustIndex = direction === 'before' ? (prevIndex < nextIndex ? -1 : 0) : prevIndex < nextIndex ? 1 : 0;
        reorderedData.splice(prevIndex, 1);
        reorderedData.splice(Math.max(nextIndex + adjustIndex, 0), 0, activeItem.current || reorderedData[0]);
        orderChanged.setTrue();
        NavigationService.legacyShowWarningLostDataModal(true);
        setGridData(reorderedData);
    };

    const dragStart = (dataItem: LibraryRiskFieldsExtended) => {
        activeItem.current = dataItem;
    };

    const updateOrders = (itemsToUpdate: LibraryRiskDisplayOrder[]) => {
        updateLibraryRisksOrder({
            variables: {
                libraryRisksDisplayOrder: itemsToUpdate,
            },
        })
            .then(() => {
                refetchData();
                onSaveReorder();
                NotificationService.showNotification(t('admin:organisationReorderRisksSuccess'));
                orderChanged.setFalse();
                NavigationService.legacyShowWarningLostDataModal(false);
            })
            .catch(() => NotificationService.showNotification(t('errorMessage')));
    };

    const onUpdateOrders = () => {
        const itemIndexesToUpdate: LibraryRiskDisplayOrder[] = [];

        gridData.forEach((item, index) => {
            if (item.displayOrder !== index + 1) {
                itemIndexesToUpdate.push({
                    displayOrder: index + 1,
                    libraryRiskId: item.id,
                });
            }
        });

        updateOrders(itemIndexesToUpdate);
    };

    const handleClose = () => {
        if (orderChanged.value) {
            showWarningModal.setTrue();
        } else {
            closeReorder();
        }
    };

    const getScrollAvailable = () => {
        const scrollableContainer = grid.current?.querySelector('.k-grid-content');
        if (scrollableContainer) {
            return scrollableContainer.scrollHeight - scrollableContainer.clientHeight;
        }
    };
    return (
        <>
            <WarningLostDataDialog
                open={showWarningModal.value}
                onClose={(action: string) => {
                    if (action === 'close') {
                        NavigationService.legacyShowWarningLostDataModal(false);
                        closeReorder();
                    }
                    showWarningModal.setFalse();
                }}
            />
            <div className="reorder-grid__heading">
                <h3 className="reorder-grid__title">{t('admin:organisationReorderRisks')}</h3>
                <div className="reorder-grid__btns-group">
                    <button type="button" className="reorder-grid__btn" onClick={() => onUpdateOrders()}>
                        <FontAwesomeIcon
                            icon={['fal', 'circle-check']}
                            className="reorder-grid__icon reorder-grid__icon--save"
                        />
                    </button>
                    <button type="button" className="reorder-grid__btn" onClick={() => handleClose()}>
                        <FontAwesomeIcon
                            icon={['fal', 'circle-xmark']}
                            className="reorder-grid__icon reorder-grid__icon--close"
                        />
                    </button>
                </div>
            </div>
            <ReorderContext.Provider value={{ reorder, dragStart }}>
                <DragAndDrop>
                    <Grid
                        data={gridData}
                        className="reorder-grid"
                        dataItemKey="displayOrder"
                        rows={{
                            data: (rowProps) => <DraggableRow scrollAvailable={getScrollAvailable()} {...rowProps} />,
                        }}
                    >
                        <GridColumn
                            title={t('ra:order')}
                            width={CUSTOM_COLUMN_WIDTH}
                            cells={{
                                data: (props: GridCustomCellProps) => DragHandleCell({ ...props, firstCol: true }),
                            }}
                        />
                        {columns.map((column) => (
                            <GridColumn
                                {...column}
                                width={setWidth(column.minWidth)}
                                title={column.label ?? undefined}
                                cells={{ data: DragHandleCell }}
                            />
                        ))}
                    </Grid>
                </DragAndDrop>
            </ReorderContext.Provider>
        </>
    );
};

export default RisksReorderingGrid;
